var _dec, _class, _TreeBrowser;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import keycode from 'keycode';
import { IconFolderLine, IconDocumentLine } from '@instructure/ui-icons';
import { omitProps, pickProps } from '@instructure/ui-react-utils';
import { withStyle } from '@instructure/emotion';
import { TreeCollection } from "./TreeCollection/index.js";
import { TreeButton } from "./TreeButton/index.js";
import { TreeNode } from "./TreeNode/index.js";
import generateStyles from "./styles.js";
import generateComponentTheme from "./theme.js";
import { allowedProps } from "./props.js";
import TreeBrowserContext from "./TreeBrowserContext.js";
/**
---
category: components
---
**/
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
let TreeBrowser = (_dec = withStyle(generateStyles, generateComponentTheme), _dec(_class = (_TreeBrowser = class TreeBrowser extends Component {
  constructor(props) {
    super(props);
    this.ref = null;
    this.handleCollectionClick = (e, collection, expand = true) => {
      e.stopPropagation();
      const onCollectionClick = this.props.onCollectionClick;
      if (expand) this.expandOrCollapseNode(collection);
      onCollectionClick === null || onCollectionClick === void 0 ? void 0 : onCollectionClick(collection.id, collection); // TODO: this should pass the event as the first arg
      this.handleSelection(collection.id, 'collection');
    };
    this.handleItemClick = (e, item) => {
      var _this$props$onItemCli, _this$props;
      e.stopPropagation();
      (_this$props$onItemCli = (_this$props = this.props).onItemClick) === null || _this$props$onItemCli === void 0 ? void 0 : _this$props$onItemCli.call(_this$props, item);
      this.handleSelection(item.id, 'item');
    };
    this.handleKeyDown = (event, node) => {
      switch (event.keyCode) {
        case keycode.codes.down:
        case keycode.codes.j:
          event.stopPropagation();
          this.moveFocus(1);
          break;
        case keycode.codes.up:
        case keycode.codes.k:
          event.stopPropagation();
          this.moveFocus(-1);
          break;
        case keycode.codes.home:
        case keycode.codes.end:
          event.stopPropagation();
          this.homeOrEnd(event.keyCode);
          break;
        case keycode.codes.left:
        case keycode.codes.right:
          event.stopPropagation();
          this.handleLeftOrRightArrow(event.keyCode, node);
          break;
        case keycode.codes.enter:
        case keycode.codes.space:
          event.stopPropagation();
          this.handleActivation(event, node);
          break;
        default:
          return;
      }
      event.preventDefault();
    };
    this.getIsFlattened = collection => {
      const _this$props2 = this.props,
        rootId = _this$props2.rootId,
        showRootCollection = _this$props2.showRootCollection;
      return !showRootCollection && typeof rootId !== 'undefined' && collection.id === rootId;
    };
    if (typeof this.props.expanded === 'undefined') {
      this.state = {
        expanded: this.props.defaultExpanded,
        selection: ''
      };
    } else {
      this.state = {
        selection: ''
      };
    }
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props3;
    (_this$props$makeStyle = (_this$props3 = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props3);
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props4;
    (_this$props$makeStyle2 = (_this$props4 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props4);
  }
  get _root() {
    return this.ref;
  }
  get collections() {
    const _this$props5 = this.props,
      collections = _this$props5.collections,
      rootId = _this$props5.rootId;
    if (typeof rootId !== 'undefined') {
      return [collections[rootId]];
    } else {
      return Object.keys(collections).map(id => collections[id]).filter(collection => collection != null);
    }
  }
  get expanded() {
    return this.getExpanded(this.state, this.props);
  }
  getExpanded(state, props) {
    return typeof props.expanded === 'undefined' ? state.expanded : props.expanded;
  }
  expandOrCollapseNode(collection) {
    var _this$props$onCollect, _this$props6;
    (_this$props$onCollect = (_this$props6 = this.props).onCollectionToggle) === null || _this$props$onCollect === void 0 ? void 0 : _this$props$onCollect.call(_this$props6, collection);
    if (typeof this.props.expanded === 'undefined') {
      this.setState((state, props) => {
        const expanded = [...this.getExpanded(state, props)];
        const expandedIndex = this.getExpandedIndex(expanded, collection.id);
        if (collection.expanded && expandedIndex < 0) {
          expanded.push(collection.id);
        } else if (expandedIndex >= 0) {
          expanded.splice(expandedIndex, 1);
        }
        return {
          expanded
        };
      });
    }
  }
  handleSelection(id, type) {
    const selectionType = this.props.selectionType;
    selectionType === 'single' && this.setState(state => {
      const selection = `${type}_${id}`;
      if (state.selection !== selection) {
        return {
          selection
        };
      } else {
        return state;
      }
    });
  }
  getNavigableNodes() {
    return Array.from(this.ref.querySelectorAll('[role="treeitem"]'));
  }
  moveFocus(delta) {
    const nodes = this.getNavigableNodes();
    const closest = window.document.activeElement.closest('[role="treeitem"]');
    const active = nodes.indexOf(closest);
    let next = active + delta;
    if (next < 0) {
      next = 0;
    } else if (next >= nodes.length) {
      next = nodes.length - 1;
    }
    nodes.forEach(n => {
      n.setAttribute('tabindex', '-1');
    });
    nodes[next].setAttribute('tabindex', '0');
    nodes[next].focus();
  }
  homeOrEnd(keyCode) {
    const length = this.getNavigableNodes().length;
    if (keyCode === keycode.codes.home) {
      this.moveFocus(1 - length);
    } else {
      this.moveFocus(length - 1);
    }
  }
  handleLeftOrRightArrow(keyCode, node) {
    const ltr = !(this.ref.parentElement.dir === 'rtl' || document.dir === 'rtl');
    if (ltr && keyCode === keycode.codes.left || !ltr && keyCode == keycode.codes.right) {
      this.handleCloseOrPrevious(node);
    } else {
      this.handleOpenOrNext(node);
    }
  }
  handleOpenOrNext(node) {
    if (node && !this.expanded.includes(node.id) && node.type === 'collection') {
      this.expandOrCollapseNode(node);
    } else {
      this.moveFocus(1);
    }
  }
  handleCloseOrPrevious(node) {
    if (node && this.expanded.includes(node.id) && node.type === 'collection') {
      this.expandOrCollapseNode(node);
    } else {
      this.moveFocus(-1);
    }
  }
  handleActivation(event, node) {
    if (node == null) return;
    if (node.type === 'collection') {
      this.handleCollectionClick(event, node, this.props.selectionType === 'none');
    } else {
      this.handleItemClick(event, node);
    }
  }
  getSubCollections(collection) {
    const collections = collection.collections ? [...collection.collections] : [];
    return collections.map(id => this.getCollectionProps(this.props.collections[id])).filter(collection => collection != null).sort(this.props.sortOrder);
  }
  getItems(collection) {
    if (collection.items) {
      const items = collection.items ? [...collection.items] : [];
      return items.map(id => {
        return {
          ...this.props.items[id]
        };
      }).filter(item => item != null).sort(this.props.sortOrder);
    } else {
      return [];
    }
  }
  getCollectionProps(collection) {
    return {
      id: collection.id,
      name: collection.name,
      collections: this.getSubCollections(collection),
      items: this.getItems(collection),
      descriptor: collection.descriptor,
      containerRef: collection.containerRef,
      renderBeforeItems: collection.renderBeforeItems,
      renderAfterItems: collection.renderAfterItems,
      expanded: this.getExpandedIndex(this.expanded, collection.id) >= 0,
      isCollectionFlattened: this.getIsFlattened(collection),
      compareFunc: collection.compareFunc
    };
  }
  getExpandedIndex(expanded, id) {
    return expanded.findIndex(expanded => String(expanded) === String(id));
  }
  renderRoot() {
    return this.collections.sort(this.props.sortOrder).map((collection, i) => _jsx(TreeCollection, {
      ...pickProps(omitProps(this.props), TreeCollection.allowedProps),
      ...this.getCollectionProps(collection),
      selection: this.state.selection,
      onItemClick: this.handleItemClick,
      onCollectionClick: this.handleCollectionClick,
      onKeyDown: this.handleKeyDown,
      numChildren: this.collections.length,
      level: 1,
      position: 1,
      renderContent: this.props.renderContent
    }, i));
  }
  render() {
    const styles = this.props.styles;
    return _jsx(TreeBrowserContext.Provider, {
      value: {
        animation: this.props.animation
      },
      children: _jsx("ul", {
        css: styles === null || styles === void 0 ? void 0 : styles.treeBrowser,
        tabIndex: 0,
        role: "tree",
        onKeyDown: this.handleKeyDown,
        ref: el => {
          this.ref = el;
        },
        "aria-label": this.props.treeLabel,
        "data-cid": "TreeBrowser",
        children: this.renderRoot()
      })
    });
  }
}, _TreeBrowser.displayName = "TreeBrowser", _TreeBrowser.componentId = 'TreeBrowser', _TreeBrowser.allowedProps = allowedProps, _TreeBrowser.defaultProps = {
  size: 'medium',
  variant: 'folderTree',
  showRootCollection: true,
  collectionIcon: IconFolderLine,
  collectionIconExpanded: IconFolderLine,
  itemIcon: IconDocumentLine,
  getItemProps: props => props,
  getCollectionProps: props => props,
  defaultExpanded: [],
  selectionType: 'none',
  sortOrder: function () {
    return 0;
  },
  animation: true
}, _TreeBrowser.Node = TreeNode, _TreeBrowser.Collection = TreeCollection, _TreeBrowser.Button = TreeButton, _TreeBrowser)) || _class);
export default TreeBrowser;
export { TreeBrowser };